import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { FormLabel, Stack, Typography } from '@mui/material';
import Divider from '@mui/material/Divider';
import { FormInput } from '@components/FormInput/ui/FormInput';
import Box from '@mui/material/Box';
import { LoadingButton } from '@mui/lab';

import useToast from '@hooks/useToast';
import {
  ChangePasswordFormFields,
  changePasswordValidationSchema,
} from '@features/settings-change-password/utils/form-validators';
import { passwordChangeRequest } from '@api/auth/apiService';
import MainCard from '@components/ui/cards/MainCard';
import { checkPasswordComplexity } from '@utils/utils.ts';

import styles from './index.module.scss';

function SettingsChangePasswordForm() {
  const { showSuccess, showError } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const methods = useForm<ChangePasswordFormFields>({
    resolver: yupResolver(changePasswordValidationSchema),
  });

  const {
    register,
    formState: { errors },
    watch,
    reset,
    handleSubmit,
  } = methods;

  const newPassword = watch('newPassword');

  const onSuccessSubmit = (passwordData: any) => {
    setIsSubmitting(true);
    passwordChangeRequest(
      {
        current_password: passwordData.currentPassword,
        password: passwordData.newPassword,
        password2: passwordData.confirmNewPassword,
      },
      () => {
        setIsSubmitting(false);
        showSuccess(
          'Password successfully updated',
          'Your changes have been saved.'
        );
        reset();
      },
      (response) => {
        setIsSubmitting(false);
        showError(
          'Error',
          response.response.data.message[0] ||
            'Please contact an administrator.'
        );
      }
    );
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSuccessSubmit)}>
        <MainCard>
          <Typography variant="h6" gutterBottom>
            Password
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Please enter a new password.
          </Typography>
          <Divider sx={{ marginBottom: 3 }} />
          <Stack spacing={3} divider={<Divider />}>
            <Stack direction="row" alignItems="center">
              <FormLabel sx={{ width: '314px' }}>Current password</FormLabel>
              <FormInput
                bordered
                type="password"
                placeholder="Enter current password"
                className={styles.inputWide}
                errorText={
                  errors?.currentPassword && errors?.currentPassword.message
                }
                {...register('currentPassword')}
              />
            </Stack>

            <Stack direction="row" alignItems="center">
              <FormLabel sx={{ width: '314px' }}>New password</FormLabel>
              <Box className={styles.passwordComplexityWrapper}>
                <FormInput
                  bordered
                  type="password"
                  placeholder="Create new password"
                  className={styles.inputWide}
                  errorText={errors?.newPassword && errors?.newPassword.message}
                  {...register('newPassword')}
                />
                <div
                  className={[
                    styles.complexity,
                    styles[`level-${checkPasswordComplexity(newPassword)}`],
                  ].join(' ')}
                >
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
                <p>
                  Use 8+ characters, with a mix of letters, numbers & symbols.
                </p>
              </Box>
            </Stack>

            <Stack direction="row" alignItems="center">
              <FormLabel sx={{ width: '314px' }}>
                Confirm new password
              </FormLabel>
              <FormInput
                bordered
                type="password"
                placeholder="Confirm new password"
                className={styles.inputWide}
                errorText={
                  errors?.confirmNewPassword &&
                  errors?.confirmNewPassword.message
                }
                {...register('confirmNewPassword')}
              />
            </Stack>

            <Box display="flex" justifyContent="flex-end">
              <LoadingButton
                variant="contained"
                type="submit"
                loading={isSubmitting}
              >
                Update password
              </LoadingButton>
            </Box>
          </Stack>
        </MainCard>
      </form>
    </FormProvider>
  );
}

export default SettingsChangePasswordForm;
